import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import QRCode from 'qrcode'
import Toast from '../../ReusableComponents/UI/Toast'
import { ChromeNote } from '../PhonePairNote'
import CameraStage from './CameraStage'
import OrientationToggle from './OrientationToggle'
import InsightsPanel from './InsightsPanel'
import DetectionBar from './DetectionBar'
import TrackingControls from './TrackingControls'
import TrackingOptions from './TrackingOptions'
import GestureOptions from './GestureOptions'
import ColorOptions from './ColorOptions'
import MarkerOptions from './MarkerOptions'
import useVisionLoop from './useVisionLoop'
import { speechControl } from './detectors/speechDetector'
import ColorAimBox from './ColorAimBox'
import { MOBILE_CAMERA_ID } from './cameraConfig'
import { makeRoomCode } from '../webrtc/signalingConfig'
import {
    startMobilePairing,
    stopMobilePairing,
    mobileConnected,
} from '../webrtc/mobileLink'
import styles from './Vision.module.css'

const VisionPlay = () => {
    const history = useHistory()
    const [panelOpen, setPanelOpen] = useState(true)
    const {
        videoRef,
        overlayRef,
        activeId,
        setActiveId,
        insights,
        insightsTitle,
        fps,
        loading,
        error,
        mirrored,
        toggleMirror,
        setMirror,
        devices,
        activeDeviceId,
        selectDevice,
        reattachMobile,
    } = useVisionLoop('face')

    const [pairQr, setPairQr] = useState('')
    const [pairRoom, setPairRoom] = useState('')
    const [showPair, setShowPair] = useState(false)
    const [toast, setToast] = useState('')
    const isPhone = activeDeviceId === MOBILE_CAMERA_ID
    const [linkCopied, setLinkCopied] = useState(false);

    // Text (OCR): a mirrored preview flips the letters, so OCR would read
    // garbage. Turn the mirror off automatically and tell the user why.
    useEffect(() => {
        if (activeId === 'ocr' && mirrored) {
            setMirror(false)
            setToast('Mirror turned off so text reads the right way for OCR.')
        }
    }, [activeId, mirrored, setMirror])

    // Choosing "Phone Camera": if already paired, just switch to it; otherwise
    // open a QR here so the phone can be paired without leaving play mode.
    const handleSelectDevice = async (id) => {
        if (id === MOBILE_CAMERA_ID && !mobileConnected()) {
            const code = makeRoomCode()
            setPairRoom(code)
            setShowPair(true)
            const url = `${window.location.origin}/mobilecamera?room=${code}`
            try {
                setPairQr(await QRCode.toDataURL(url, { width: 190, margin: 1 }))
            } catch (e) {
                /* code still shown */
            }
            startMobilePairing(code, (s) => {
                if (s === 'connected') {
                    setShowPair(false)
                    // Force a re-attach — on reconnect the stream is new but the
                    // device id is unchanged, so selectDevice would no-op.
                    reattachMobile()
                }
            })
            return
        }
        selectDevice(id)
    }

    const cancelPair = () => {
        stopMobilePairing()
        setShowPair(false)
        setPairQr('')
        setPairRoom('')
    }

    const goBack = () => {
        sessionStorage.setItem('slideDirection', true)
        // history.push('/webAiPlay')
        history.push('/Selection')
    }

    return (
        <div className={styles.play}>
            <CameraStage
                videoRef={videoRef}
                overlayRef={overlayRef}
                mirrored={mirrored}
                fps={fps}
                devices={devices}
                activeDeviceId={activeDeviceId}
                onSelectDevice={handleSelectDevice}
                onToggleMirror={toggleMirror}
                loading={loading}
            />

            <button
                type="button"
                className={styles.backBtn}
                onClick={goBack}
                aria-label="Back"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M15 5l-7 7 7 7"
                        stroke="#fff"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            <button
                type="button"
                className={`${styles.panelToggle} ${panelOpen ? styles.panelToggleOpen : ''}`}
                onClick={() => setPanelOpen((v) => !v)}
                aria-label="Toggle panel"
            >
                {panelOpen ? '›' : '‹'}
            </button>

            {isPhone && <OrientationToggle className={styles.orientToggle} />}

            {activeId === 'tracking' && (
                <TrackingControls videoRef={videoRef} mirrored={mirrored} />
            )}

            {activeId === 'color' && <ColorAimBox />}

            {showPair && (
                <div className={styles.pairOverlay} onClick={cancelPair}>
                    <div
                        className={styles.pairModal}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.pairHeader}>
                            <h3 className={styles.pairTitle}>
                                Connect your phone camera
                            </h3>
                            <p className={styles.pairSubtitle}>
                                Use your phone as a camera for this device.
                            </p>
                        </div>

                        <div className={styles.pairBody}>
                            <div className={styles.pairQrCol}>
                                <div className={styles.pairQrBox}>
                                    {pairQr ? (
                                        <img
                                            src={pairQr}
                                            alt="Scan to connect your phone"
                                            className={styles.pairQrImg}
                                        />
                                    ) : (
                                        <span className={styles.pairQrLoading}>
                                            Loading...
                                        </span>
                                    )}
                                </div>

                                <div className={styles.pairCode}>
                                    Code{' '}
                                    <strong className={styles.pairCodeValue}>
                                        {pairRoom}
                                    </strong>
                                </div>
                            </div>

                            <div className={styles.pairSteps}>
                                <div className={styles.pairStep}>
                                    <div className={styles.pairStepNum}>1</div>
                                    <div>
                                        <div className={styles.pairStepTitle}>
                                            Scan the QR code with your phone
                                        </div>
                                        <div className={styles.pairStepDesc}>
                                            Your phone will open the camera page automatically.
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.pairDivider} />

                                <div className={styles.pairStep}>
                                    <div className={styles.pairStepNum}>2</div>
                                    <div>
                                        <div className={styles.pairStepTitle}>
                                            Open the link in Google Chrome
                                        </div>
                                        <div className={styles.pairStepDesc}>
                                            Chrome provides the best camera support.
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.pairNotes}>
                                    <div className={styles.pairNote}>
                                        <span className={styles.pairNoteIcon}>i</span>
                                        <span>
                                            Keep your phone and this device on the same Wi-Fi network.
                                        </span>
                                    </div>
                                </div>

                                <div className={styles.pairLinkSection}>
                                    <div className={styles.pairLinkHead}>
                                        <span className={styles.pairLinkLabel}>
                                            Can't scan the QR code?
                                        </span>

                                        <button
                                            type="button"
                                            className={styles.pairCopyBtn}
                                            onClick={async () => {
                                                const url =
                                                    `https://edu.plode.org/mobilecamera?room=${pairRoom}`;

                                                try {
                                                    await navigator.clipboard.writeText(url);

                                                    setLinkCopied(true);

                                                    setTimeout(() => {
                                                        setLinkCopied(false);
                                                    }, 2000);
                                                } catch (error) {
                                                    console.error('Failed to copy link:', error);
                                                }
                                            }}
                                        >
                                            {linkCopied ? '✓ Copied' : 'Copy link'}
                                        </button>
                                    </div>

                                    <div className={styles.pairUrl}>
                                        https://edu.plode.org/mobilecamera?room={pairRoom}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.pairFooter}>
                            <button
                                type="button"
                                className={styles.pairCancelBtn}
                                onClick={cancelPair}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {error && <div className={styles.errorBadge}>{error}</div>}

            {/* Options + insights share one panel on the right. */}
            <div
                className={`${styles.sidePanel} ${panelOpen ? '' : styles.sidePanelHidden
                    }`}
            >
                {activeId === 'tracking' && <TrackingOptions />}
                {activeId === 'gesture' && <GestureOptions />}
                {activeId === 'color' && <ColorOptions />}
                {activeId === 'marker' && <MarkerOptions />}
                {activeId === 'speech' && (
                    <button
                        type="button"
                        className={styles.trackLineBtn}
                        onClick={() => speechControl.reset()}
                    >
                        Reset transcript
                    </button>
                )}
                {activeId === 'ocr' && (
                    <p className={styles.ocrHint}>
                        Mirror is kept off for Text (OCR) so letters read the
                        right way round. The text updates live and clears when
                        nothing readable is in view.
                    </p>
                )}
                {activeId !== 'color' && <InsightsPanel title={insightsTitle} insights={insights} />}
            </div>

            {/* Detection picker along the bottom, matching the /ai-code icons. */}
            <DetectionBar activeId={activeId} onSelect={setActiveId} />

            <Toast
                show={!!toast}
                message={toast}
                onClose={() => setToast('')}
            />
        </div >
    )
}

export default VisionPlay
